import React, { useEffect, useMemo, useState } from 'react';
import { Tabs } from 'antd';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useHistory, useRouteMatch } from 'react-router-dom';
import {
  defaultResponseHandler,
  Page,
  useAssetConfig,
  useAssetParams,
  useApiFilters,
  useUserinfo,
} from '@mss/mss-component-library';
import {
  assetModel,
  exportAssetTypeSubTypeKey,
  paramsToExportAssetType,
} from './Import/util';
import Breadcrumbs from 'jsx/common/Breadcrumbs';
import AssetSelectionTab from 'jsx/asset/components/BulkOperations/AssetSelectionTab';
import BulkEditTab from 'jsx/asset/components/BulkOperations/BulkEditTab';
import BulkDeleteTab from 'jsx/asset/components/BulkOperations/BulkDeleteTab';
import { subtypeToCoreAssetName } from 'jsx/asset/components/BulkOperations/assetTypeMapping';
import PageSidebar from 'jsx/common/FilterSidebar';
import Fetch from 'jsx/common/util/_fetch';
import { getModelNameForCoreAsset } from 'jsx/authorization/util/objectPermissions';
import { sanitizedUrl } from 'jsx/asset/util/sanitize';
import type { ExportAssetType } from 'jsx/asset/util/useAddPermissions';

const BULK_PAGE_SIZE = 10;

/** Newest first; rows without created_at sort last (matches API default -created_at). */
function sortBulkRowsByCreatedAtDesc(rows: any[]): any[] {
  const ms = (row: any): number | null => {
    const v = row?.created_at;
    if (v == null || v === '') return null;
    const t = new Date(v).getTime();
    return Number.isNaN(t) ? null : t;
  };
  return [...rows].sort((a, b) => {
    const ta = ms(a);
    const tb = ms(b);
    if (ta == null && tb == null) return 0;
    if (ta == null) return 1;
    if (tb == null) return -1;
    return tb - ta;
  });
}

type Mode = 'edit' | 'delete';

type Props = {
  mode: Mode;
};

const BulkOperationPage: React.FC<Props> = ({ mode }) => {
  const { t } = useTranslation(['asset', 'common']);
  const history = useHistory();
  const { url } = useRouteMatch();
  const { assetType } = useAssetParams();
  const { config } = useAssetConfig();
  // URL has plcdcs, assetmanagement, etc.; API and config use dcs_plc, asset_management, etc.
  const typeForSubtype = paramsToExportAssetType(String(assetType)) as ExportAssetType;

  const [activeTab, setActiveTab] = useState<'selection' | 'action'>('selection');
  const [selectedAssets, setSelectedAssets] = useState<any[]>([]);
  const [assetsCount, setAssetsCount] = useState(0);

  const filterApi = useApiFilters(t, assetsCount, BULK_PAGE_SIZE);

  const [hasPermission] = useUserinfo();

  const coreAssetNames = useMemo(() => {
    const info = exportAssetTypeSubTypeKey(typeForSubtype);
    const options = info?.options ?? [];
    return [...new Set(options.map((opt: string) => subtypeToCoreAssetName(opt)))];
  }, [typeForSubtype]);

  const permPrefix = mode === 'edit' ? 'change' : 'delete';
  const permittedCoreAssetNames = useMemo(() => {
    if (coreAssetNames.length <= 1) return coreAssetNames;
    const permitted = coreAssetNames.filter((name) => {
      const modelName = getModelNameForCoreAsset(name);
      if (!modelName) return true;
      return hasPermission(`${permPrefix}_${modelName}`);
    });
    return permitted.length > 0 ? permitted : coreAssetNames;
  }, [coreAssetNames, permPrefix, hasPermission]);

  const bulkPath = mode === 'edit' ? 'bulk/edit' : 'bulk/delete';

  const assetsQuery = useQuery({
    queryKey: ['bulkAssets', mode, typeForSubtype, permittedCoreAssetNames],
    queryFn: async () => {
      if (permittedCoreAssetNames.length === 0) {
        return { count: 0, results: [] };
      }

      const fetchOne = async (name: string) => {
        const base = `/api/core/asset-type/${name}/${bulkPath}/`;
        const url = `${base}?page_size=2500`;
        const resp = await defaultResponseHandler<{
          count: number;
          results: any[];
        } | any[]>({
          t,
          request: Fetch.get(url),
          customErrorHandling: permittedCoreAssetNames.length > 1 ? () => {} : undefined,
        });
        if (resp.error || resp.data == null) {
          return { count: 0, results: [] };
        }
        const d = resp.data;
        const isPaginated =
          d && typeof d === 'object' && 'results' in d;
        const results = isPaginated ? (d as any).results ?? [] : (d as any[]);
        const count = isPaginated ? (d as any).count ?? 0 : results.length;
        return { count, results };
      };

      if (permittedCoreAssetNames.length === 1) {
        return fetchOne(permittedCoreAssetNames[0]);
      }

      const responses = await Promise.all(
        permittedCoreAssetNames.map((name) => fetchOne(name))
      );

      const merged = responses.reduce(
        (acc, r) => ({
          count: acc.count + r.count,
          results: [...acc.results, ...r.results],
        }),
        { count: 0, results: [] as any[] }
      );

      return merged;
    },
    keepPreviousData: true,
  });

  const FIELD_ASSET_UP_STATUSES = ['20', '40', '50'];
  const FIELD_ASSET_DOWN_STATUSES = ['10', '30'];
  const FIELD_ASSET_OFF_STATUSES = ['0'];

  const getAssetState = (row: any): string => {
    const heartbeatState = row.heartbeat?.state;
    const statusIcon = row.asset_data?.status_icon;
    if (heartbeatState === 'up' || (statusIcon && FIELD_ASSET_UP_STATUSES.includes(String(statusIcon)))) return 'up';
    if (heartbeatState === 'down' || (statusIcon && FIELD_ASSET_DOWN_STATUSES.includes(String(statusIcon)))) return 'down';
    return 'off';
  };

  const rawResults = assetsQuery.data?.results ?? [];
  const filteredResults = useMemo(() => {
    const search = filterApi.search?.toLowerCase().trim() ?? '';
    const selected = filterApi.selectedFilters ?? {};

    const filtered = rawResults.filter((row: any) => {
      if (search) {
        const baseSearchable = [
          row.name,
          row.role,
          row.location,
          (row.custodian as any)?.name,
        ];
        const extraKeys = config?.listView?.extraSearchKeys ?? [];
        const searchable = [...baseSearchable, ...extraKeys.map((k: string) => row[k])]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!searchable.includes(search)) return false;
      }

      const custodianId = (row.custodian as any)?.id;

      const getRowValue = (key: string): any => {
        if (key === 'custodian') return custodianId;
        if (key === 'communication_type') return row.asset_data?.communication_type ?? row.communication_type;
        if (key === 'wireless') return row.wireless;
        if (key === 'prm') return row.prm;
        if (key === 'asset_state') return getAssetState(row);
        return row[key];
      };

      const checkFilter = (key: string) => {
        const vals = selected[key];
        if (!vals?.length) return true;
        const rowVal = getRowValue(key);
        return (
          vals.includes(rowVal) ||
          (vals.includes('(null)') && (rowVal === undefined || rowVal === null))
        );
      };

      if (!checkFilter('role')) return false;
      if (!checkFilter('custodian')) return false;
      if (!checkFilter('location')) return false;
      if (!checkFilter('priority')) return false;
      const stKey =
        exportAssetTypeSubTypeKey(typeForSubtype)?.key ?? 'application_type';
      if (!checkFilter(String(stKey))) return false;
      if (!checkFilter('communication_type')) return false;
      if (!checkFilter('wireless')) return false;
      if (!checkFilter('prm')) return false;
      if (!checkFilter('asset_state')) return false;

      return true;
    });

    return sortBulkRowsByCreatedAtDesc(filtered);
  }, [
    rawResults,
    typeForSubtype,
    config?.listView?.extraSearchKeys,
    filterApi.search,
    filterApi.selectedFilters,
  ]);

  useEffect(() => {
    setAssetsCount(filteredResults.length);
  }, [filteredResults.length]);

  const filtersQuery = useQuery({
    queryKey: ['bulkAssets', 'filters', typeForSubtype],
    queryFn: async () => {
      const resp = await defaultResponseHandler<Record<string, { label: string; options: { label: string; value: string }[] }>>({
        t,
        request: Fetch.get(
          `/api/assets/filters/?type=${typeForSubtype}&deleted=false`
        ),
      });
      return resp.data;
    },
    onSuccess: data => filterApi.updateFilters(data as any),
  });

  const availableFilters = useMemo(() => {
    const data = filtersQuery.data;
    if (!data || typeof data !== 'object') return {};
    type FilterValue = { label: string; options: { label: string; value: string }[] };
    return Object.fromEntries(
      Object.entries(data).filter(
        ([, v]) => (v as FilterValue)?.options?.length > 1
      )
    );
  }, [filtersQuery.data]);

  const subtypeInfo = exportAssetTypeSubTypeKey(typeForSubtype);
  const subtypeKey = subtypeInfo ? String(subtypeInfo.key) : 'application_type';
  const subtypeLabels = subtypeInfo?.labels ?? {};
  const singleSubtypeFallback =
    subtypeInfo?.options?.length === 1 ? subtypeInfo.options[0] : undefined;
  const customFieldModel = assetModel[typeForSubtype];
  const assetTypeName = config?.name ?? typeForSubtype;

  const title =
    mode === 'edit' ? t('Bulk edit assets') : t('Bulk delete assets');
  const pageTitle = `${assetTypeName} / ${title}`;

  const queryClient = useQueryClient();
  const [invalidateAfterPageReset, setInvalidateAfterPageReset] = useState(false);

  const handleNext = () => setActiveTab('action');
  const handlePrevious = () => setActiveTab('selection');
  const handleComplete = () => {
    setSelectedAssets([]);
    setActiveTab('selection');
    history.push(sanitizedUrl(url));
  };

  const handleBulkDeleteSuccess = () => {
    filterApi.goToPage(1);
    setInvalidateAfterPageReset(true);
    handleComplete();
  };

  useEffect(() => {
    if (invalidateAfterPageReset && filterApi.params?.page === 1) {
      queryClient.invalidateQueries(['fieldAssets']);
      queryClient.invalidateQueries(['bulkAssets']);
      setInvalidateAfterPageReset(false);
    }
  }, [invalidateAfterPageReset, filterApi.params?.page, queryClient]);

  return (
    <Page
      breadCrumbs={<Breadcrumbs />}
      padding={false}
      main={
        <div className="flex flex-col h-full px-2 pb-6">
          <h1 className="text-xl font-normal leading-10 mt-6 mb-4 mx-0">
            {pageTitle}
          </h1>
          <Tabs
            activeKey={activeTab}
            onChange={key => setActiveTab(key as 'selection' | 'action')}
            className="bulk-operation-tabs"
            items={[
              {
                key: 'selection',
                label: t('Asset selection'),
                children: (
                  <div className="pt-4 pb-4 flex gap-4">
                    <div className="flex-1 min-w-0">
                      <h2 className="text-base font-medium mb-4">
                        {t('Select assets for bulk actions')}
                      </h2>
                      <AssetSelectionTab
                        selectedAssets={selectedAssets}
                        onSelectionChange={setSelectedAssets}
                        onNext={handleNext}
                        type={typeForSubtype}
                        subtypeKey={subtypeKey}
                        subtypeLabels={subtypeLabels}
                        singleSubtypeFallback={singleSubtypeFallback}
                        dataSource={filteredResults}
                        loading={assetsQuery.isLoading}
                        pagination={filterApi.pagination}
                        onTableChange={filterApi.onChangeAssetTable}
                      />
                    </div>
                    <div className="w-56 shrink-0">
                      <h2 className="text-base font-medium mb-4">
                        {t('Search / Filters')}
                      </h2>
                      <PageSidebar
                        loading={filtersQuery.isFetching}
                        availableFilters={availableFilters}
                        toggleFilter={filterApi.toggleFilter}
                        selectedFilters={filterApi.selectedFilters}
                        search={{
                          value: filterApi.search,
                          set: filterApi.setSearch,
                        }}
                      />
                    </div>
                  </div>
                ),
              },
              {
                key: 'action',
                label: t('Define action'),
                disabled: selectedAssets.length === 0,
                children: (
                  <div className="pt-4 pb-4">
                    {mode === 'edit' ? (
                    <BulkEditTab
                      selectedAssets={selectedAssets}
                      onPrevious={handlePrevious}
                      onComplete={handleComplete}
                      type={typeForSubtype}
                      subtypeKey={subtypeKey}
                      singleSubtypeFallback={singleSubtypeFallback}
                      customFieldModel={customFieldModel}
                    />
                  ) : (
                    <BulkDeleteTab
                      selectedAssets={selectedAssets}
                      onPrevious={handlePrevious}
                      onComplete={handleBulkDeleteSuccess}
                      type={typeForSubtype}
                      subtypeKey={subtypeKey}
                    />
                    )}
                  </div>
                ),
              },
            ]}
          />
        </div>
      }
    />
  );
};

export default BulkOperationPage;
